import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// --- Types ---

export interface ScoringBreakdown {
  cup_pressure: number;
  future_cost_pressure: number;
  staleness_risk: number;
  care_path_deviation: number;
  data_gap_penalty: number;
  staleness_escalation: number;
  base_score: number;
  weights: Record<string, number>;
  component_source_map: Record<string, string>;
  red_flags: string[];
}

export type RiskTier = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'UNKNOWN';
export type RecommendedAction =
  | 'CONTACT_CLIENT'
  | 'CONFIRM_COVERAGE_LIMITS'
  | 'ESCALATE_CARE'
  | 'CONTINUE_CONSERVATIVE_CARE'
  | 'CONTINUE_AGGRESSIVE_CARE'
  | 'MOVE_TO_DEMAND_REVIEW'
  | 'URGENT_ATTORNEY_ESCALATION';
export type EstimatedFlag = 'NONE' | 'PARTIAL' | 'HEAVY';
export type FeedbackThumb = 'UP' | 'DOWN';
export type PolicySearchUrgency = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type CoverageTier = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
export type ClientSentiment = 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
export type SentimentTrend = 'IMPROVING' | 'STABLE' | 'DECLINING';
export type SentimentSensitivity = 'LOW_SENSITIVITY' | 'MODERATE_SENSITIVITY' | 'HIGH_SENSITIVITY';

export type CitationSourceType =
  | 'checkin'
  | 'checkin_notes'
  | 'artifact'
  | 'sms'
  | 'expense'
  | 'provider'
  | 'encounter'
  | 'prior_snapshot'
  | 'web_research'
  | 'unknown';

export interface EnrichedCitation {
  source_type: CitationSourceType;
  source_id: string;
  display_name: string;
  artifact_id: string | null;
  matter_id: string | null;
  url: string | null;
}

// v2 types
export type ForkState = 'PRE_FORK' | 'PATH_A' | 'PATH_B';
export type MilestoneStatus = 'ON_TRACK' | 'AT_RISK' | 'OVERDUE' | 'BLOCKED' | 'NOT_APPLICABLE';
export type PDSeverity = 'MINOR' | 'MODERATE' | 'SEVERE' | 'UNKNOWN';
export type PainProgressionState = 'WORSENING' | 'STABLE' | 'IMPROVING' | 'ABSENT' | 'STALE';
export type ActionUrgency = 'STANDARD' | 'IMMEDIATE' | 'DEFERRED';

export interface RiskSnapshotSchema {
  id: string;
  matter_id: string;

  // Timing
  run_started_at: string | null;
  run_finished_at: string | null;
  input_snapshot_at: string | null;
  input_fingerprint: string | null;

  // Policy
  policy_version_id: string | null;
  policy_published_at: string | null;
  model_version: string | null;

  // Coverage
  coverage_tier: CoverageTier | null;
  limit_for_scoring: string | null;
  limit_source: string | null;
  limit_confidence: number | null;

  // Outputs
  risk_score: number | null;
  risk_tier: RiskTier | null;
  confidence: number | null;
  recommended_action: RecommendedAction | null;
  low_confidence_urgent: boolean;
  estimated_flag: EstimatedFlag | null;
  stale_or_incomplete: boolean;
  scoring_breakdown: ScoringBreakdown | null;

  // Evidence
  summary_short: string | null;
  facts_used: string[];
  assumptions: string[];
  missing_inputs: string[];
  confidence_reasoning: string | null;
  citations: string[];
  citations_enriched: EnrichedCitation[] | null;

  // Cost / limits
  confirmed_insurance_limit: string | null;
  estimated_insurance_limit: string | null;
  confirmed_cost_so_far: string | null;
  estimated_cost_so_far: string | null;
  estimated_projected_future_cost: string | null;
  projected_over_limit_amount: string | null;

  // Client net projection
  gross_recovery_estimate: string | null;
  projected_medical_total: string | null;
  client_net_estimate: string | null;
  client_net_ratio: number | null;
  net_projection_below_25: boolean;

  // Client signals
  client_refusal_on_record: boolean;
  client_refusal_reason: string | null;
  client_refusal_recorded_at: string | null;

  // Policy search
  policy_search_recommended: boolean;
  policy_search_urgency: PolicySearchUrgency | null;
  policy_search_triggers: string[];
  policy_search_requires_firm_approval: boolean;

  // Sentiment
  client_sentiment: ClientSentiment | null;
  client_sentiment_reasoning: string | null;
  client_sentiment_trend: SentimentTrend | null;
  negative_sentiment_count: number;
  client_sensitivity: SentimentSensitivity | null;

  // v2: Care-path state
  fork_state: ForkState | null;
  milestone_status: MilestoneStatus | null;
  pd_severity: PDSeverity | null;
  pain_progression_state: PainProgressionState | null;
  pain_progression_source: string | null;
  pain_progression_source_date: string | null;
  pain_progression_staleness: string | null;
  pain_progression_flags: string[];

  // v2: Action resolution
  secondary_actions: string[];
  suppressed_actions: Record<string, unknown>[];
  action_refire_log: Record<string, unknown>[];
  action_urgency: ActionUrgency | null;

  // v2: Client net extended
  client_net_confidence: number | null;
  client_net_inputs: Record<string, unknown> | null;
  client_net_flags: string[];

  // Feedback
  feedback_thumb: FeedbackThumb | null;
  feedback_text: string | null;
  feedback_updated_by_id: string | null;
  feedback_updated_at: string | null;

  // Timestamps
  created_at: string | null;
  modified_at: string | null;
}

interface RiskSnapshotFeedbackRequest {
  feedback_thumb: FeedbackThumb;
  feedback_text?: string;
}

// --- Risk Overview types ---

export interface RiskOverviewMatterRow {
  matter_id: string;
  matter_name: string;
  status: string;
  firm_id: string;
  firm_name: string;
  representing_attorney_name: string | null;
  managing_paralegal_name: string | null;
  risk_score: number | null;
  risk_tier: RiskTier | null;
  recommended_action: RecommendedAction | null;
  confidence: number | null;
  stale_or_incomplete: boolean;
  low_confidence_urgent: boolean;
  estimated_flag: EstimatedFlag | null;
  policy_search_recommended: boolean;
  policy_search_urgency: PolicySearchUrgency | null;
  net_projection_below_25: boolean;
  missing_inputs_count: number;
  summary_short: string | null;
  coverage_tier: CoverageTier | null;
  limit_for_scoring: string | null;
  limit_source: string | null;
  snapshot_created_at: string | null;
  date_of_loss: string | null;
  matter_created_at: string | null;
  client_sentiment: ClientSentiment | null;
  client_sentiment_reasoning: string | null;
  client_sentiment_trend: SentimentTrend | null;
  negative_sentiment_count: number;
  client_sensitivity: SentimentSensitivity | null;

  // v2 fields
  fork_state: ForkState | null;
  milestone_status: MilestoneStatus | null;
  pd_severity: PDSeverity | null;
  pain_progression_state: PainProgressionState | null;
  action_urgency: ActionUrgency | null;
}

export interface RiskOverviewAggregates {
  total_matters_with_snapshots: number;
  avg_score: number | null;
  count_critical: number;
  count_high: number;
  count_medium: number;
  count_low: number;
  count_unknown: number;
  count_incomplete: number;
}

export interface RiskOverviewResponse {
  aggregates: RiskOverviewAggregates;
  matters: RiskOverviewMatterRow[];
}

export interface RiskOverviewParams {
  firm?: string[];
  representing_attorney?: string[];
  managing_paralegal?: string[];
  status?: string[];
  risk_tier?: RiskTier[];
}

// --- API ---

export const mobrainApiClient = createApi({
  reducerPath: 'mobrainApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['RiskSnapshot', 'RiskSnapshots'] as const,
  endpoints: (builder) => ({
    getLatestRiskSnapshot: builder.query<RiskSnapshotSchema, string>({
      query: (matterId) => `api/matters/${matterId}/risk-snapshots/latest/`,
      extraOptions: { skipSentryOnStatus: [404] },
      providesTags: (result, error, matterId) => [
        { type: 'RiskSnapshot', id: matterId },
      ],
    }),

    listRiskSnapshots: builder.query<RiskSnapshotSchema[], string>({
      query: (matterId) => `api/matters/${matterId}/risk-snapshots/`,
      providesTags: (result, error, matterId) => [
        { type: 'RiskSnapshots', id: matterId },
      ],
    }),

    submitRiskSnapshotFeedback: builder.mutation<
      RiskSnapshotSchema,
      { matterId: string; feedback: RiskSnapshotFeedbackRequest }
    >({
      query: ({ matterId, feedback }) => ({
        url: `api/matters/${matterId}/risk-snapshots/feedback/`,
        method: 'POST',
        body: feedback,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'RiskSnapshot', id: matterId },
        { type: 'RiskSnapshots', id: matterId },
        'RiskSnapshot',
      ],
    }),

    triggerRiskAssessment: builder.mutation<
      { status: string; matter_id: string },
      string
    >({
      query: (matterId) => ({
        url: `api/matters/${matterId}/risk-snapshots/trigger/`,
        method: 'POST',
      }),
    }),

    getRiskOverview: builder.query<RiskOverviewResponse, RiskOverviewParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.firm) {
          params.firm.forEach((f) => searchParams.append('firm', f));
        }
        if (params.managing_paralegal) {
          params.managing_paralegal.forEach((p) =>
            searchParams.append('managing_paralegal', p),
          );
        }
        if (params.representing_attorney) {
          params.representing_attorney.forEach((a) =>
            searchParams.append('representing_attorney', a),
          );
        }
        if (params.status) {
          params.status.forEach((s) => searchParams.append('status', s));
        }
        if (params.risk_tier) {
          params.risk_tier.forEach((t) => searchParams.append('risk_tier', t));
        }
        const qs = searchParams.toString();
        return `api/risk-overview/${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['RiskSnapshot'],
    }),
  }),
});

export const {
  useGetLatestRiskSnapshotQuery,
  useListRiskSnapshotsQuery,
  useSubmitRiskSnapshotFeedbackMutation,
  useTriggerRiskAssessmentMutation,
  useGetRiskOverviewQuery,
} = mobrainApiClient;
